import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Tag, Space, Modal, Select, message } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, CopyOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { DataTable } from '../../components';
import { useEmailTemplates } from '../../hooks/useEmailTemplates';
import type { EmailTemplate } from '../../services/email-template.service';

const STATE_ACTIVE = 1;
const STATE_INACTIVE = 0;

export const EmailTemplateList: React.FC = () => {
  const navigate = useNavigate();
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);

  const { templates, loading, meta, filters, setFilters, refetch, deleteManyTemplates, cloneTemplate } = useEmailTemplates();

  const handleSearch = (value: string) => {
    setFilters({ page: 1, limit: 20, search: value, state: filters.state });
  };

  const handleStateFilter = (value: number | undefined) => {
    setFilters({ page: 1, limit: 20, search: filters.search, state: value });
  };

  const handlePageChange = (page: number, pageSize: number) => {
    setFilters({ ...filters, page, limit: pageSize });
  };

  const handleClone = (id: string, name: string) => {
    Modal.confirm({
      title: 'Nhân bản template',
      content: `Nhân bản template "${name}"?`,
      okText: 'Nhân bản',
      cancelText: 'Huỷ',
      onOk: async () => {
        try {
          await cloneTemplate(id);
          message.success('Nhân bản thành công');
          setSelectedRowKeys([]);
        } catch {
          message.error('Nhân bản thất bại');
        }
      },
    });
  };

  const handleDeleteOne = (id: string, name: string) => {
    Modal.confirm({
      title: 'Xoá template',
      content: `Xoá template "${name}"?`,
      okText: 'Xoá',
      okType: 'danger',
      cancelText: 'Huỷ',
      onOk: async () => {
        try {
          await deleteManyTemplates([id]);
          message.success('Đã xoá');
          setSelectedRowKeys([]);
        } catch {
          message.error('Xoá thất bại');
        }
      },
    });
  };

  const handleDeleteMany = () => {
    if (selectedRowKeys.length === 0) return;
    Modal.confirm({
      title: 'Xoá các template đã chọn',
      content: `Xoá ${selectedRowKeys.length} template?`,
      okText: 'Xoá',
      okType: 'danger',
      cancelText: 'Huỷ',
      onOk: async () => {
        try {
          await deleteManyTemplates(selectedRowKeys as string[]);
          setSelectedRowKeys([]);
          message.success('Đã xoá');
        } catch {
          message.error('Xoá thất bại');
        }
      },
    });
  };

  const columns: ColumnsType<EmailTemplate> = [
    {
      title: 'Mã template',
      dataIndex: 'code',
      key: 'code',
      width: 180,
      render: (code: string) => <span style={{ fontFamily: 'monospace', fontSize: 12 }}>{code}</span>,
    },
    {
      title: 'Tên',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: 'Tiêu đề email',
      dataIndex: 'subject',
      key: 'subject',
      ellipsis: true,
    },
    {
      title: 'Ngày tạo',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 150,
      render: (date: string) => dayjs(date).format('DD/MM/YYYY HH:mm'),
    },
    {
      title: 'Trạng thái',
      dataIndex: 'state',
      key: 'state',
      width: 120,
      render: (state: number) => (
        <Tag color={state === STATE_ACTIVE ? 'success' : 'error'}>
          {state === STATE_ACTIVE ? 'Hoạt động' : 'Tắt'}
        </Tag>
      ),
    },
  ];

  const filterContent = (
    <Select
      placeholder="Trạng thái"
      allowClear
      style={{ width: 160 }}
      value={filters.state}
      onChange={handleStateFilter}
    >
      <Select.Option value={STATE_ACTIVE}>Hoạt động</Select.Option>
      <Select.Option value={STATE_INACTIVE}>Tắt</Select.Option>
    </Select>
  );

  return (
    <DataTable
      title="Template email"
      data={templates}
      columns={columns}
      loading={loading}
      rowKey="_id"
      searchable
      searchPlaceholder="Tìm theo tên hoặc mã..."
      onSearch={handleSearch}
      onRefresh={refetch}
      filters={filterContent}
      pagination={{
        current: meta.page,
        pageSize: meta.limit,
        total: meta.total,
        onChange: handlePageChange,
      }}
      selectionType="checkbox"
      selectedRowKeys={selectedRowKeys}
      onSelectionChange={setSelectedRowKeys}
      extraActions={
        <Space>
          {selectedRowKeys.length > 0 && (
            <Button danger icon={<DeleteOutlined />} onClick={handleDeleteMany}>
              Xoá đã chọn ({selectedRowKeys.length})
            </Button>
          )}
          <Button type="primary" icon={<PlusOutlined />} onClick={() => navigate('/email-templates/add')}>
            Thêm mới
          </Button>
        </Space>
      }
      actionColumn={{
        width: 150,
        fixed: 'right',
        actions: (record: EmailTemplate) => [
          {
            key: 'edit',
            label: 'Sửa',
            icon: <EditOutlined />,
            onClick: () => navigate(`/email-templates/${record._id}/edit`),
          },
          {
            key: 'clone',
            label: 'Nhân bản',
            icon: <CopyOutlined />,
            onClick: () => handleClone(record._id, record.name),
          },
          {
            key: 'delete',
            label: 'Xoá',
            icon: <DeleteOutlined />,
            danger: true,
            onClick: () => handleDeleteOne(record._id, record.name),
          },
        ],
      }}
    />
  );
};

export default EmailTemplateList;
